/**
 * 3 Nov 2026 (D-1103-NEXT) — "Do next" at the top of Today: one list in order of importance (shared/do-next.ts), one
 * sentence and one button a line. It merges what Today already reads — Needs your attention and Might slip — and adds no
 * read of its own. The button opens the place the source line already opened; nothing is done or sent from the list.
 * Seven lines, then "Show all N". The grouped view stays one press away under "See them by program" (closed by default).
 */
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DO_NEXT_SHOWN, DO_NEXT_TIER_KEY, buildDoNext, doNextCount, type DoNextEntry } from '@shared/do-next';
import type { HouseAttnLine } from '@shared/house-today';
import { MIGHT_SLIP_KINDS, mightSlipRole } from '@shared/might-slip';
import type { Role } from '@shared/roles';
import { useOpenIn, type Program } from '@/app/program';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { AttentionGroups } from './AttentionGroups';
import { MightSlip, slipWords, useMightSlip } from './MightSlip';
import { entryWords } from './entryWords';
import '@/styles/today1025.css';

function OpenLink({ href, program, children, ...rest }: { href: string; program: Program | null; children: ReactNode; className?: string; 'data-testid'?: string; 'aria-label'?: string }) {
  const openIn = useOpenIn();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openIn(href, program);
  };
  return (
    <Link to={href} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

export function DoNext({ attention, role, todayKey }: { attention: readonly HouseAttnLine[]; role: Role | null | undefined; todayKey: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const slip = useMightSlip(role);
  const slipOn = mightSlipRole(role) && !!slip.data?.enabled;
  const entries = useMemo(() => buildDoNext(attention, slipOn ? (slip.data?.lines ?? []) : [], todayKey), [attention, slipOn, slip.data, todayKey]);
  const total = doNextCount(entries);
  const [all, setAll] = useState(false);
  const [byProgram, setByProgram] = useState(false);

  // one polite word, only when the count changes after the first reading
  const before = useRef<number | null>(null);
  useEffect(() => {
    if (before.current !== null && before.current !== total) announce(total ? t('doNext1103.changed', { count: total }) : t('doNext1103.none'));
    before.current = total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const shown = all ? entries : entries.slice(0, DO_NEXT_SHOWN);
  const unavailable = (slipOn ? (slip.data?.unavailable ?? []) : []).filter((k) => (MIGHT_SLIP_KINDS as readonly string[]).includes(k)).map((k) => t(`slip1102.kind.${k}`));

  const words = (e: DoNextEntry): string => (e.attn ? entryWords(e.attn, t, lang) : slipWords(e.slip!, t, lang));
  const action = (e: DoNextEntry): string => (e.slip ? t(`slip1102.action.${e.slip.kind}`) : t('doNext1103.open'));

  return (
    <>
      <section aria-labelledby="h-donext1103" className="t184-section donext1103" data-testid="donext1103">
        <h2 id="h-donext1103">{total ? t('doNext1103.titleCount', { count: total }) : t('doNext1103.title')}</h2>
        {entries.length ? (
          <>
            <ol className="slip1102-lines donext1103-lines" aria-labelledby="h-donext1103">
              {shown.map((e) => {
                const w = words(e);
                const a = action(e);
                const tier = t(`doNext1103.tier.${DO_NEXT_TIER_KEY[e.tier]}`);
                return (
                  <li key={e.id} className="slip1102-line" data-tier={e.tier} data-kind={e.kind} data-testid={`donext1103-line-${e.id}`}>
                    <span className="donext1103-tier">{tier}</span>{' '}
                    <span className="slip1102-what">{w}</span>
                    {e.href ? (
                      <OpenLink href={e.href} program={e.openIn} className="btn secondary sm slip1102-go" aria-label={`${a} — ${w}`} data-testid={`donext1103-open-${e.id}`}>
                        {a}
                      </OpenLink>
                    ) : null}
                  </li>
                );
              })}
            </ol>
            {entries.length > DO_NEXT_SHOWN ? (
              <Button size="sm" variant="secondary" aria-expanded={all} onClick={() => setAll((v) => !v)} data-testid="donext1103-toggle">
                {all ? t('slip1102.fewer') : t('slip1102.all', { count: entries.length })}
              </Button>
            ) : null}
          </>
        ) : (
          <p className="help" data-testid="donext1103-none">
            {t('doNext1103.none')}
          </p>
        )}
        {unavailable.length ? (
          <p className="help" role="status" data-testid="donext1103-unavailable">
            {t('slip1102.unavailable', { list: unavailable.join(', ') })}
          </p>
        ) : null}
        <div className="donext1103-more">
          <Button size="sm" variant="secondary" aria-expanded={byProgram} aria-controls="donext1103-grouped" onClick={() => setByProgram((v) => !v)} data-testid="donext1103-byprogram">
            {t('doNext1103.byProgram')}
          </Button>
        </div>
      </section>
      {byProgram ? (
        <div id="donext1103-grouped" data-testid="donext1103-grouped">
          <AttentionGroups lines={attention} />
          <MightSlip role={role} />
        </div>
      ) : null}
    </>
  );
}
